import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import Button from './Button';
import { useRegisterOverlay } from '@/contexts/OverlayStackContext';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'xl';
  position?: 'center' | 'bottom' | 'responsive';
  hideCloseButton?: boolean;
  className?: string;
  children: React.ReactNode;
  btnOKText?: string;
  btnCancelText?: string;
  okAction?: () => void;
  cancelAction?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  position = 'responsive',
  hideCloseButton = false,
  className = '',
  children,
  btnOKText = '',
  btnCancelText = '',
  okAction,
  cancelAction,
}) => {
  useRegisterOverlay(isOpen, onClose);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    xl: 'max-w-4xl',
  };

  const isBottomMode = position === 'bottom' || position === 'responsive';

  return (
    <div
      className={`fixed inset-0 z-50 flex ${
        isBottomMode ? 'items-end sm:items-center' : 'items-center'
      } justify-center p-0 sm:p-4 bg-black/50 transition-opacity duration-200`}
      onClick={onClose}
    >
      <div
        className={`
          relative bg-white ${
            isBottomMode ? 'rounded-t-[32px] sm:rounded-2xl' : 'rounded-2xl'
          } shadow-2xl w-full
          ${sizeClasses[size]}
          max-h-[92vh] sm:max-h-[90vh] overflow-hidden
          transform transition-all duration-300 ease-out
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle Bar for Bottom Mode */}
        {isBottomMode && (
          <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-3 mb-1 sm:hidden" />
        )}

        {/* Header */}
        {(title || subtitle) && (
          <div className="flex items-start justify-between px-6 pt-3 pb-4 sm:py-4 border-b border-gray-100">
            <div>
              {title && <h2 className="text-lg sm:text-xl font-extrabold text-[#0E1726]">{title}</h2>}
              {subtitle && <p className="text-xs text-gray-500 font-medium mt-0.5">{subtitle}</p>}
            </div>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors duration-200 shrink-0 ml-3 text-gray-500 cursor-pointer"
                aria-label="Close modal"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {!title && !subtitle && !hideCloseButton && (
          <div className="flex justify-end px-4 pt-3">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors duration-200 shrink-0 text-gray-500 cursor-pointer"
              aria-label="Close modal"
            >
              <Icon icon="ph:x-bold" className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">{children}</div>

        {/* Footer */}
        {(btnOKText || btnCancelText) && (
          <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-200">
            {btnCancelText && (
              <Button
                variant="outline"
                onClick={() => {
                  if (cancelAction) cancelAction();
                  onClose();
                }}
              >
                {btnCancelText}
              </Button>
            )}
            {btnOKText && (
              <Button
                variant="primary"
                onClick={() => {
                  if (okAction) okAction();
                  onClose();
                }}
                className=""
              >
                {btnOKText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
